import { useMemo } from 'react';

import { usableCustomerIds } from '@features/products/counts/countFilters';
import { mapAggregateCounts } from '@features/products/counts/mapAggregateCounts';
import type { ProductCountsInput, ProductCountsResult } from '@features/products/counts/productCounts';

import { useGetGuestRidesCountRowsQuery } from './getGuestRidesCountRows.query.generated';
import { useGetGuestRidesCountsQuery } from './getGuestRidesCounts.query.generated';
import { buildGuestRidesCountsVariables } from './guestRidesCountsVariables';

/** The rows document runs only when the per-customer breakdown is asked for. */
export const useGuestRidesCounts = ({ customerIds, window, breakdown = false, skip = false, pollOptions }: ProductCountsInput): ProductCountsResult => {
  const ids = usableCustomerIds(customerIds);
  const idsKey = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids' content, not the array, is the input
  const variables = useMemo(() => buildGuestRidesCountsVariables({ customerIds: ids, window }), [idsKey, window?.startIso, window?.endIso]);
  const runCounts = !skip && ids.length > 0;
  const runRows = runCounts && breakdown;
  const common = { variables, fetchPolicy: 'cache-and-network' as const, ...pollOptions };

  const counts = useGetGuestRidesCountsQuery({ ...common, skip: !runCounts });
  const rows = useGetGuestRidesCountRowsQuery({ ...common, skip: !runRows });

  const mapped = useMemo(
    () => (counts.data ? mapAggregateCounts(counts.data, runRows ? rows.data : undefined, 'guest-rides', 'rides') : undefined),
    [counts.data, rows.data, runRows],
  );

  return {
    counts: mapped,
    loading: (counts.loading && !counts.data) || (runRows && rows.loading && !rows.data),
    error: counts.error ?? rows.error,
    // Only the queries that run are refetched; a skipped query's refetch would run it network-only.
    refetch: () => {
      if (runCounts) void counts.refetch();
      if (runRows) void rows.refetch();
    },
  };
};
